import { FC, Suspense } from 'react';
import { Route, Routes } from 'react-router-dom';

import { AnonRoutes, KioskCatchAll, KioskRoutes } from '@app/kiosk/src/routes/AccessKiosk';

import { PAGES } from '@lib/tools/views/urls';
import { lazyImport } from '@lib/tools/views/utils';
import { ErrorPageNotFound } from '@lib/tools/views/web/components/ErrorPageNotFound';

const HomeContainer = lazyImport(() => import('@app/kiosk/src/containers/HomeContainer'), 'HomeContainer');
const LoginContainer = lazyImport(() => import('@app/kiosk/src/containers/KioskLogin'), 'KioskLoginContainer');
const ProductContainer = lazyImport(() => import('@app/kiosk/src/containers/ProductContainer'), 'ProductContainer');
const ResultCatalogContainer = lazyImport(
  () => import('@app/kiosk/src/containers/ResultCatalogContainer'),
  'ResultCatalogContainer',
);
const SearchCatalogContainer = lazyImport(
  () => import('@app/kiosk/src/containers/SearchCatalogContainer'),
  'SearchCatalogContainer',
);
const WelcomeContainer = lazyImport(() => import('@app/kiosk/src/containers/WelcomeContainer'), 'WelcomeContainer');
const QuizChooseExpertiseContainer = lazyImport(
  () => import('@app/web/src/containers/QuizChooseExpertiseContainer'),
  'QuizChooseExpertiseContainer',
);
const LegalTabsContainer = lazyImport(
  () => import('@lib/tools/shared/legals/views/containers/LegalTabsContainer'),
  'LegalTabsContainer',
);
const AgeAndGenderContainer = lazyImport(
  () => import('@lib/tools/shared/quizzes/views/containers/AgeAndGenderContainer'),
  'AgeAndGenderContainer',
);
const FoodHabitsContainer = lazyImport(
  () => import('@lib/tools/shared/quizzes/views/containers/FoodHabitsContainer'),
  'FoodHabitsContainer',
);
const QuizContainer = lazyImport(
  () => import('@lib/tools/shared/quizzes/views/containers/QuizContainer'),
  'QuizContainer',
);
const FoodPairingContainer = lazyImport(
  () => import('@lib/tools/shared/recipes/views/containers/FoodPairingContainer'),
  'FoodPairingContainer',
);

const Router: FC = () => {
  const RouteGroup = (c, ...paths) => {
    return paths.map(p => <Route key={p} Component={c} path={p} />);
  };

  const kioskBasename = '/:lang/:retailer/loc/:location/p/:product';

  const retailerUrl = url => kioskBasename + url;

  return (
    <Suspense fallback={null}>
      <Routes>
        <Route Component={AnonRoutes}>
          <Route key="urlLogin" Component={LoginContainer} path={PAGES.vinhood.kioskLogin} />
        </Route>

        <Route path={kioskBasename}>
          <Route Component={KioskRoutes}>
            <Route key="urlWelcome" Component={WelcomeContainer} path={`${retailerUrl(PAGES.vinhood.welcome)}`} />

            <Route key="urlHome" Component={HomeContainer} path={retailerUrl(PAGES.vinhood.home)} />

            {RouteGroup(QuizContainer, retailerUrl(`/quiz/:quizType/`))}

            <Route
              key="urlTasteOrExpertSelection"
              Component={QuizChooseExpertiseContainer}
              path={retailerUrl(PAGES.vinhood.quiz.chooseExpert)}
            />

            <Route
              key="urlResultCatalog"
              Component={ResultCatalogContainer}
              path={retailerUrl(PAGES.vinhood.catalog)}
            />

            <Route
              key="urlSearchCatalog"
              Component={SearchCatalogContainer}
              path={retailerUrl(PAGES.vinhood.searchCatalog)}
            />

            <Route key="urlProduct" Component={ProductContainer} path={retailerUrl(PAGES.vinhood.product)} />

            <Route
              key="urlCommonFoodHabits"
              Component={FoodHabitsContainer}
              path={retailerUrl(PAGES.vinhood.quiz.chooseTaste)}
            />

            <Route
              key="urlFoodPairing"
              Component={FoodPairingContainer}
              path={retailerUrl(PAGES.vinhood.recipeSearch)}
            />

            <Route
              key="urlAgeAndGender"
              Component={AgeAndGenderContainer}
              path={retailerUrl(PAGES.vinhood.ageAndGender)}
            />

            <Route Component={KioskCatchAll}>
              <Route key="urlCatchAllToHome" Component={HomeContainer} path="*" />
            </Route>
          </Route>

          {/* Common routes */}
          <Route key="urlCommonLegal" Component={LegalTabsContainer} path={retailerUrl(PAGES.vinhood.legal.tabs)} />

          <Route key="urlErrorNotFound" Component={ErrorPageNotFound} path={retailerUrl(PAGES.error.notFound)} />
        </Route>

        <Route Component={KioskCatchAll}>
          <Route key="urlCatchAllToLogin" Component={LoginContainer} path="*" />
        </Route>
      </Routes>
    </Suspense>
  );
};

export default Router;
